import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import { quoteApi, quoteSupplierItemApi } from '@/main/api';
import BoxCart from '@/main/components/shared/boxCart/boxCart';
import BoxCartNoSupplierAssigned from '@/main/components/shared/boxCart/boxCartNoSupplierAssigned';
import BoxCartUnansweredItems from '@/main/components/shared/boxCart/boxCartUnansweredItems';
import {
  AddCartType,
  RefreshQuoteByGetItemsType
} from '@/main/components/shared/boxCart/components/types';
import ResultNotFoundComponent from '@/main/components/shared/resultNotFound/resultNotFoundComponent';
import { SpinnerLoading } from '@/main/components/shared/styles';
import {
  QuoteGetItemsFilterRequest,
  QuoteSupplierItemSaveCartRequest
} from '@/main/models/request';
import { ProcurementStatusEnum } from '@/main/services/enum/StatusProcurement';
import {
  getTranslateByKey,
  isEmpty,
  isNullUndefined,
  isShowControlByElementId
} from '@/main/services/utils';
import { userIsSupplier } from '@/main/services/utilsUser';
import { formatCurrency } from '@/main/services/utilsNumber';
import { Cart, CartState } from '@/main/store/cart';
import {
  calculateTotalParts,
  calculateTotalPrice,
  calculateTotalShippingCost,
  destructGetItemsResponse,
  transformCartItemsToCart
} from '@/main/store/cart/cartUtils';
import InfoShippingCostAndTotal from '../../../createorder/components/infoShippingCostAndTotal/InfoShippingCostAndTotal';
import { RoutesConstants } from '@/main/routes/routesConstants';
import { onLoadDateRangePicker } from '@/main/services/utilsDate';
import { useParameterByTypeScreenReducer } from '@/main/store/parameterByTypeScreen';
import { ParameterTypeEnum } from '@/main/services/enum/ParameterTypeEnum';
import { Controls } from '@/main/services/constants/constantsControl';

import {
  handleUpdateQuoteCountStatusType,
  handleUpdateQuoteGridStatusType
} from '../cardGrid/types';
import FooterCollapseQuote from './footerCollapseQuote/footerCollapseQuote';
import HeaderCollapseQuote from './headerCollapseQuote/headerCollapseQuote';
import { DataQuoteStateType } from './typesCollapseQuote';
import { INITIAL_CART_STATE } from './utilsCollapseQuoteStep3';
import * as S from './styles';
import BoxCartUnansweredCanceledItems from '@/main/components/shared/boxCart/boxCartUnansweredCanceledItems';
import BoxCartUnansweredRequotedItems from '@/main/components/shared/boxCart/boxCartUnansweredRequotedItems';

interface ICollapseQuoteStep3Props {
  guidQuote: string;
  handleUpdateQuoteCountStatus: handleUpdateQuoteCountStatusType;
  handleUpdateQuoteGridStatus: handleUpdateQuoteGridStatusType;
  refreshGrid?: () => void;
  guidClaim: string;
}
function CollapseQuoteStep3({
  guidQuote,
  handleUpdateQuoteCountStatus,
  handleUpdateQuoteGridStatus,
  refreshGrid = () => { },
  guidClaim
}: ICollapseQuoteStep3Props) {
  const navigate = useNavigate();
  const parameterRangeDays = useParameterByTypeScreenReducer(
    ParameterTypeEnum.DefaultSearchOrderDaysInterval
  );

  const [dataQuote, setDataQuote] = useState<DataQuoteStateType>(null);
  const [dataCart, setDataCart] = useState<CartState>(INITIAL_CART_STATE);
  const [isLoadingQuote, setIsLoadingQuote] = useState<boolean>(false);

  const cartItems = dataCart.cartItems.data;
  const cartNoSupplierAssigned = dataCart.cartItems.dataNoSupplierAssigned;
  const cartDataUnansweredItems = dataCart.cartItems.dataUnansweredItems;
  const cartDataUnansweredCanceledItems =
    dataCart.cartItems.dataUnansweredCanceledItems;
  const cartDataUnansweredRequotedItems =
    dataCart.cartItems.dataUnansweredRequotedItems;
  const cancelParts = isShowControlByElementId(Controls.IdCancelPart);

  async function getDetailsQuote(
    guidQuoteDetails: string,
    showLoading: boolean = false
  ) {
    if (showLoading) setIsLoadingQuote(true);

    try {
      const response = await quoteApi.detailAwaiting(guidQuoteDetails);
      const {
        data: { result }
      } = response;

      const hasChangeStatus =
        !isNullUndefined(dataQuote) &&
        dataQuote.idProcurementStatus !== result.idProcurementStatus;

      if (hasChangeStatus) {
        if (result.idProcurementStatus === ProcurementStatusEnum.Closed) {
          const { firstDateResult, secondDateResult } =
            onLoadDateRangePicker(parameterRangeDays);
          const urlRefresh = `${RoutesConstants.orderSearchOrder}?quoteFilterVehicle=${result.claimNumber}&startDate=${firstDateResult.toISOString()}&finalDate=${secondDateResult.toISOString()}`;
          navigate(urlRefresh);
          return;
        }

        handleUpdateQuoteGridStatus(
          result.guid,
          result.idProcurementStatus,
          result.nameProcurementStatus
        );
        handleUpdateQuoteCountStatus();
      }

      setDataQuote(result);
    } catch (error) {
    } finally {
      if (showLoading) setIsLoadingQuote(false);
    }
  }

  async function loadCart() {
    const request: QuoteGetItemsFilterRequest = {
      quoteGuid: guidQuote
    };

    const response = await quoteApi.getItems(request);
    const {
      dataCart,
      dataCartNoSupplierAssigned,
      dataCartUnansweredItems,
      dataCartUnansweredCanceledItems,
      dataCartUnansweredRequotedItems
    } = destructGetItemsResponse(response.data.result);

    const formattedDataCart = transformCartItemsToCart(dataCart, guidQuote);
    const updatedTotalSaved = calculateTotalPrice(formattedDataCart);
    const updatedShippingCostSaved =
      calculateTotalShippingCost(formattedDataCart);
    const updatedTotalPartsSaved = calculateTotalParts(formattedDataCart);
    const resultState: CartState = {
      errorMessagesGeneral: null,
      cartItems: {
        isLoading: false,
        isLoadingUpdate: false,
        idPartIsLoading: null,
        errorMessages: null,
        isError: false,
        data: formattedDataCart,
        dataNoSupplierAssigned: dataCartNoSupplierAssigned,
        dataUnansweredItems: dataCartUnansweredItems,
        dataUnansweredCanceledItems: dataCartUnansweredCanceledItems,
        dataUnansweredRequotedItems: dataCartUnansweredRequotedItems
      },
      summary: {
        total: updatedTotalSaved,
        shippingCost: updatedShippingCostSaved,
        totalParts: updatedTotalPartsSaved
      }
    };

    setDataCart(resultState);
  }
  async function loadCartWithLoading() {
    setIsLoadingQuote(true);
    try {
      await loadCart();
    } catch (error) {
    } finally {
      setIsLoadingQuote(false);
    }
  }

  async function loadInitialData() {
    if (isEmpty(guidQuote)) return;
    if (!isNullUndefined(dataQuote) && !isNullUndefined(dataCart)) return;
    setIsLoadingQuote(true);

    try {
      const response = await Promise.all([
        getDetailsQuote(guidQuote),
        loadCart()
      ]);

      return response;
    } catch (error) {
    } finally {
      setIsLoadingQuote(false);
    }
  }

  useEffect(() => {
    loadInitialData();
  }, [guidQuote]);

  const isQuoteOpen =
    !isNullUndefined(dataQuote) &&
    dataQuote.idProcurementStatus === ProcurementStatusEnum.Open;
  const isQuoteBlocked = !isNullUndefined(dataQuote) && dataQuote.quoteBlocked;
  const isQuoteCanceled =
    !isNullUndefined(dataQuote) &&
    dataQuote.idProcurementStatus === ProcurementStatusEnum.Cancelled;
  const integration = !isNullUndefined(dataQuote) && dataQuote.integration;
  const isAwaitingReleaseSupplier =
    !isNullUndefined(dataQuote) &&
    userIsSupplier() &&
    dataQuote.idProcurementStatus === ProcurementStatusEnum.WaitingRelease;

  const totalPriceCart = dataCart.summary.total;
  const totalCart = totalPriceCart;

  // Notation: the variable isPaymentPending is not used in this scope;
  const isPaymentPending = false;
  // Notation: this method is not used in this scope;
  function displayCompareSuppliers() { }

  const handleAddCart: AddCartType = async (items, guidQuoteSupplierItem) => {
    try {
      const request: QuoteSupplierItemSaveCartRequest = {
        guid: items.parts[0].guid,
        externalPartId: items.parts[0].externalPartId,
        deliveryBySupplier: items.parts[0].deliveryBySupplier,
        guidPart: items.parts[0].guidPart,
        guidQuote: items.guidQuote,
        guidSupplier: items.guid,
        idPartType: items.parts[0].idPartType,
        guidSupplierInventoryWarehouse:
          items.parts[0].guidSupplierInventoryWarehouse,
        quantity: items.parts[0].quantity
      };

      const response = await quoteSupplierItemApi.saveCart(request);

      if (response.data.result) {
        loadCartWithLoading();
      }
    } catch (exception) { }
  };

  const refreshQuoteByGetItems: RefreshQuoteByGetItemsType = result => {
    const {
      dataCart,
      dataCartNoSupplierAssigned,
      dataCartUnansweredItems,
      dataCartUnansweredCanceledItems,
      dataCartUnansweredRequotedItems
    } = destructGetItemsResponse(result);

    const formattedPart = transformCartItemsToCart(dataCart, String(guidQuote));
    const updatedTotalSaved = calculateTotalPrice(formattedPart);
    const updatedShippingCostSaved = calculateTotalShippingCost(formattedPart);
    const updatedTotalPartsSaved = calculateTotalParts(formattedPart);
    const resultState: CartState = {
      errorMessagesGeneral: null,
      cartItems: {
        isLoading: false,
        isLoadingUpdate: false,
        idPartIsLoading: null,
        errorMessages: null,
        isError: false,
        data: formattedPart,
        dataNoSupplierAssigned: dataCartNoSupplierAssigned,
        dataUnansweredItems: dataCartUnansweredItems,
        dataUnansweredCanceledItems: dataCartUnansweredCanceledItems,
        dataUnansweredRequotedItems: dataCartUnansweredRequotedItems
      },
      summary: {
        total: updatedTotalSaved,
        shippingCost: updatedShippingCostSaved,
        totalParts: updatedTotalPartsSaved
      }
    };

    setDataCart(resultState);

    const isCanceledQuote =
      isEmpty(dataCart) &&
      isEmpty(dataCartNoSupplierAssigned) &&
      isEmpty(dataCartUnansweredItems);

    if (isCanceledQuote) {
      getDetailsQuote(guidQuote);

      toast.success(getTranslateByKey('information.successfullyCanceledQuote'));
    }

    return {
      isCanceledQuote
    };
  };

  if (isLoadingQuote) return <SpinnerLoading />;

  if (isNullUndefined(dataQuote)) return <ResultNotFoundComponent />;

  return (
    <S.Container>
      <HeaderCollapseQuote
        dataQuote={dataQuote}
        dataCart={dataCart}
        loadCart={loadCartWithLoading}
        getDetailsQuote={getDetailsQuote}
        refreshQuoteByGetItems={refreshQuoteByGetItems}
        canShowButtonRemovePart={cancelParts && !isAwaitingReleaseSupplier}
        guidClaim={guidClaim}
      />
      {/* Items - No answered */}
      <BoxCartUnansweredItems
        dataCart={dataCart}
        integration={integration}
        idProcurementStatus={dataQuote.idProcurementStatus}
        guidQuote={String(guidQuote)}
        isPaymentPending={isPaymentPending}
        isQuoteBlocked={isQuoteBlocked}
        cart={cartDataUnansweredItems}
        loadCart={() => loadCart()}
        addCart={handleAddCart}
        refreshQuoteByGetItems={refreshQuoteByGetItems}
        showButtonRemoveBulkPart={cancelParts && !isAwaitingReleaseSupplier}
        showButtonRequoteBulkPart={!isAwaitingReleaseSupplier}
      />

      {/* Items - NoSupplierAssigned */}
      <BoxCartNoSupplierAssigned
        dataCart={dataCart}
        idProcurementStatus={dataQuote.idProcurementStatus}
        integration={integration}
        guidQuote={String(guidQuote)}
        isPaymentPending={isPaymentPending}
        isQuoteBlocked={isQuoteBlocked}
        cart={cartNoSupplierAssigned}
        canShowButtonRemovePart={cancelParts && !isAwaitingReleaseSupplier}
        canShowButtonRequotePart={false}
        loadCart={() => loadCart()}
        addCart={handleAddCart}
        refreshQuoteByGetItems={refreshQuoteByGetItems}
      />

      {/* Items - Cart */}
      {cartItems.map((part: Cart, index: number) => (
        <BoxCart
          key={index}
          cart={part}
          dataCart={dataCart}
          showCompareSuppliers={false}
          canShowDeleteBoutton={cancelParts && !isAwaitingReleaseSupplier}
          guidQuote={guidQuote}
          isPaymentPending={isPaymentPending}
          isQuoteBlocked={isQuoteBlocked}
          displayCompareSuppliers={displayCompareSuppliers}
          isQuoteCanceled={isQuoteCanceled}
          refreshQuoteByGetItems={refreshQuoteByGetItems}
          idProcurementStatus={dataQuote.idProcurementStatus}
          integration={integration}
          addCart={handleAddCart}
        />
      ))}

      <BoxCartUnansweredCanceledItems
        dataCart={dataCart}
        integration={integration}
        idProcurementStatus={dataQuote.idProcurementStatus}
        guidQuote={String(guidQuote)}
        isPaymentPending={isPaymentPending}
        isQuoteBlocked={isQuoteBlocked}
        cart={cartDataUnansweredCanceledItems}
        showButtonRequoteBulkPart={false}
        loadCart={() => loadCart()}
        addCart={handleAddCart}
        refreshQuoteByGetItems={refreshQuoteByGetItems}
      />

      <BoxCartUnansweredRequotedItems
        cart={cartDataUnansweredRequotedItems}
        loadCart={() => loadCart()}
      />

      <InfoShippingCostAndTotal total={formatCurrency(totalCart)} />

      <FooterCollapseQuote
        guidQuote={guidQuote}
        dataQuote={dataQuote}
        dataCart={dataCart}
        canShowDeleteButton={cancelParts}
        getCart={loadCart}
        getDetailsQuote={getDetailsQuote}
        refreshGrid={refreshGrid}
      />
    </S.Container>
  );
}

export default CollapseQuoteStep3;
